import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { useDebounce } from '../../hooks/useDebounce';
import axios from '../../utils/axios';
import {
  ClockIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowPathIcon,
  ShoppingCartIcon,
  CheckCircleIcon,
  XCircleIcon,
  TruckIcon,
  CreditCardIcon,
  CalendarIcon,
  TagIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  BookmarkIcon
} from '@heroicons/react/24/outline';

const OrderHistoryPage = () => {
  const { user } = useAuth();
  const { addToCart } = useCart();
  
  // State management
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  
  // Filter states
  const [searchInput, setSearchInput] = useState('');
  const [productSearchInput, setProductSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 500);
  const debouncedProductSearch = useDebounce(productSearchInput, 500);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    date_from: '',
    date_to: '',
    product_search: '',
    page: 1,
    limit: 10
  });
  
  // UI states
  const [showFilters, setShowFilters] = useState(false);
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [reorderLoading, setReorderLoading] = useState(new Set());
  const [createTemplateLoading, setCreateTemplateLoading] = useState(new Set());

  // Fetch orders
  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await axios.get(`/api/orders/history/my-orders?${params}`);
      setOrders(response.data.orders);
      setPagination(response.data.pagination);
    } catch (err) {
      setError('שגיאה בטעינת ההזמנות');
    } finally {
      setLoading(false);
    }
  };

  // Sync debounced search values into filters
  useEffect(() => {
    setFilters(prev => ({ ...prev, search: debouncedSearch, page: 1 }));
  }, [debouncedSearch]);

  useEffect(() => {
    setFilters(prev => ({ ...prev, product_search: debouncedProductSearch, page: 1 }));
  }, [debouncedProductSearch]);

  useEffect(() => {
    fetchOrders();
  }, [filters]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filtering
    }));
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  // Toggle order expansion
  const toggleOrderExpansion = (orderId) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedOrders(newExpanded);
  };

  // Handle order selection for bulk operations
  const toggleOrderSelection = (orderId) => {
    const newSelected = new Set(selectedOrders);
    if (newSelected.has(orderId)) {
      newSelected.delete(orderId);
    } else {
      newSelected.add(orderId);
    }
    setSelectedOrders(newSelected);
  };

  // Reorder single order
  const handleReorder = async (orderId) => {
    try {
      setReorderLoading(prev => new Set(prev).add(orderId));
      
      const response = await axios.post(`/api/orders/${orderId}/reorder`);
      
      if (response.data.success) {
        // Add items to cart
        for (const item of response.data.reorderItems) {
          await addToCart(item.product, item.quantity);
        }
        
        // Show success message
        const totalItems = response.data.reorderItems.length;
        const unavailableItems = response.data.unavailableItems.length;
        
        let message = `${totalItems} פריטים נוספו לעגלה`;
        if (unavailableItems > 0) {
          message += ` (${unavailableItems} פריטים לא זמינים)`;
        }
        
        window.alert(message);
      }
    } catch (err) {
      console.error('Reorder error:', err);
      window.alert('שגיאה בהוספת פריטים לעגלה');
    } finally {
      setReorderLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  // Bulk reorder
  const handleBulkReorder = async () => {
    if (selectedOrders.size === 0) return;
    
    try {
      setLoading(true);
      
      const response = await axios.post('/api/orders/bulk-reorder', {
        order_ids: Array.from(selectedOrders)
      });
      
      if (response.data.success) {
        // Add items to cart
        for (const item of response.data.reorderItems) {
          await addToCart(item.product, item.quantity);
        }
        
        // Show success message
        const totalItems = response.data.reorderItems.length;
        const unavailableItems = response.data.unavailableItems.length;
        
        let message = `${totalItems} פריטים נוספו לעגלה מ-${selectedOrders.size} הזמנות`;
        if (unavailableItems > 0) {
          message += ` (${unavailableItems} פריטים לא זמינים)`;
        }
        
        window.alert(message);
        setSelectedOrders(new Set());
      }
    } catch (err) {
      console.error('Bulk reorder error:', err);
      window.alert('שגיאה בהוספת פריטים לעגלה');
    } finally {
      setLoading(false);
    }
  };

  // Create template from order
  const handleCreateTemplate = async (orderId) => {
    const templateName = window.prompt('הכנס שם לתבנית:');
    if (!templateName) return;
    
    try {
      setCreateTemplateLoading(prev => new Set(prev).add(orderId));
      
      const response = await axios.post(`/api/order-templates/from-order/${orderId}`, {
        name: templateName,
        description: `תבנית שנוצרה מהזמנה #${orders.find(o => o.id === orderId)?.order_number || orderId}`
      });
      
      if (response.data.template) {
        window.alert('התבנית נוצרה בהצלחה!');
      }
    } catch (err) {
      console.error('Create template error:', err);
      window.alert('שגיאה ביצירת התבנית');
    } finally {
      setCreateTemplateLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  // Status display helpers
  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      shipped: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'ממתין',
      processing: 'בעיבוד',
      shipped: 'נשלח',
      delivered: 'נמסר',
      cancelled: 'בוטל',
      refunded: 'הוחזר'
    };
    return labels[status] || status;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatPrice = (price) => {
    return `₪${parseFloat(price).toFixed(2)}`;
  };

  if (loading && orders.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-32 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Header - Mobile First */}
        <div className="mb-4 sm:mb-6 lg:mb-8">
          <div className="space-y-4 lg:space-y-0 lg:flex lg:items-center lg:justify-between">
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 flex items-center">
                <ClockIcon className="h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8 ml-2 sm:ml-3 text-blue-600" />
                היסטוריית הזמנות
              </h1>
              <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600">
                נהל את ההזמנות שלך והזמן מחדש בקלות
              </p>
            </div>
            
            {/* Bulk Actions - Stack on mobile */}
            {selectedOrders.size > 0 && (
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
                <span className="text-xs sm:text-sm text-gray-600 text-center">
                  {selectedOrders.size} הזמנות נבחרו
                </span>
                <button
                  onClick={handleBulkReorder}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:bg-gray-400 text-white px-4 py-2.5 sm:py-2 rounded-lg text-sm font-medium flex items-center justify-center touch-manipulation"
                >
                  <ShoppingCartIcon className="h-4 w-4 ml-2" />
                  הזמן הכל מחדש
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Filters - Mobile First */}
        <div className="bg-white rounded-lg shadow mb-4 sm:mb-6">
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center text-gray-700 hover:text-gray-900 w-full sm:w-auto justify-between sm:justify-start touch-manipulation"
            >
              <div className="flex items-center">
                <FunnelIcon className="h-5 w-5 ml-2" />
                <span className="text-sm sm:text-base">סינון וחיפוש</span>
              </div>
              {showFilters ? (
                <ChevronUpIcon className="h-5 w-5" />
              ) : (
                <ChevronDownIcon className="h-5 w-5" />
              )}
            </button>
          </div>
          
          {showFilters && (
            <div className="px-4 sm:px-6 py-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                {/* Search */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    חיפוש הזמנה
                  </label>
                  <div className="relative">
                    <MagnifyingGlassIcon className="h-5 w-5 absolute right-3 top-2.5 sm:top-3 text-gray-400" />
                    <input
                      type="text"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      placeholder="מספר הזמנה, מעקב..."
                      className="w-full pr-10 pl-3 py-2.5 sm:py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Status */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    סטטוס
                  </label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="w-full px-3 py-2.5 sm:py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">כל הסטטוסים</option>
                    <option value="pending">ממתין</option>
                    <option value="processing">בעיבוד</option>
                    <option value="shipped">נשלח</option>
                    <option value="delivered">נמסר</option>
                    <option value="cancelled">בוטל</option>
                    <option value="refunded">הוחזר</option>
                  </select>
                </div>

                {/* Date From */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    מתאריך
                  </label>
                  <input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => handleFilterChange('date_from', e.target.value)}
                    className="w-full px-3 py-2.5 sm:py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Date To */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    עד תאריך
                  </label>
                  <input
                    type="date"
                    value={filters.date_to}
                    onChange={(e) => handleFilterChange('date_to', e.target.value)}
                    className="w-full px-3 py-2.5 sm:py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Product Search */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  חיפוש לפי מוצר
                </label>
                <div className="relative">
                  <TagIcon className="h-5 w-5 absolute right-3 top-2.5 sm:top-3 text-gray-400" />
                    <input
                    type="text"
                    value={productSearchInput}
                    onChange={(e) => setProductSearchInput(e.target.value)}
                    placeholder="שם מוצר, SKU..."
                    className="w-full pr-10 pl-3 py-2.5 sm:py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
              <div className="mr-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Orders List */}
        {orders.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              לא נמצאו הזמנות
            </h3>
            <p className="text-gray-600">
              {filters.search || filters.status || filters.product_search
                ? 'נסה לשנות את הפילטרים'
                : 'עדיין לא ביצעת הזמנות'}
            </p>
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {orders.map((order) => (
              <div key={order.id} className="bg-white rounded-lg shadow">
                {/* Order Header - Mobile First */}
                <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 border-b border-gray-200">
                  <div className="space-y-3 sm:space-y-0 sm:flex sm:items-center sm:justify-between">
                    <div className="flex items-center gap-2 sm:gap-3 lg:gap-4">
                      {/* Selection Checkbox */}
                      <input
                        type="checkbox"
                        checked={selectedOrders.has(order.id)}
                        onChange={() => toggleOrderSelection(order.id)}
                        className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded touch-manipulation flex-shrink-0"
                      />
                      
                      {/* Order Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                          הזמנה #{order.order_number}
                        </h3>
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3 lg:gap-4 text-xs sm:text-sm text-gray-600 mt-1">
                          <span className="flex items-center">
                            <CalendarIcon className="h-3 w-3 sm:h-4 sm:w-4 ml-1" />
                            {formatDate(order.created_at || order.createdAt)}
                          </span>
                          <span className="flex items-center font-medium">
                            <CreditCardIcon className="h-3 w-3 sm:h-4 sm:w-4 ml-1" />
                            {formatPrice(order.total_amount)}
                          </span>
                          <span className={`px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                            {getStatusLabel(order.status)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Actions - Stack on mobile, row on desktop */}
                    <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
                      {/* Reorder Info - Hidden on mobile */}
                      {order.reorderInfo && (
                        <div className="hidden lg:block text-xs text-gray-500 text-left">
                          <div>{order.reorderInfo.uniqueProducts} מוצרים</div>
                          <div>{order.reorderInfo.totalItems} יחידות</div>
                          {order.reorderInfo.unavailableItems > 0 && (
                            <div className="text-red-500">
                              {order.reorderInfo.unavailableItems} לא זמינים
                            </div>
                          )}
                        </div>
                      )}

                      {/* Create Template Button - Icon only on mobile */}
                      <button
                        onClick={() => handleCreateTemplate(order.id)}
                        disabled={createTemplateLoading.has(order.id) || !order.reorderInfo?.canReorder}
                        className="bg-yellow-600 hover:bg-yellow-700 active:bg-yellow-800 disabled:bg-gray-400 text-white px-2.5 sm:px-3 py-2 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium flex items-center touch-manipulation"
                        title="צור תבנית"
                      >
                        {createTemplateLoading.has(order.id) ? (
                          <ArrowPathIcon className="h-4 w-4 sm:ml-1 animate-spin" />
                        ) : (
                          <BookmarkIcon className="h-4 w-4 sm:ml-1" />
                        )}
                        <span className="hidden sm:inline">צור תבנית</span>
                      </button>

                      {/* Reorder Button - Icon only on mobile */}
                      <button
                        onClick={() => handleReorder(order.id)}
                        disabled={reorderLoading.has(order.id) || !order.reorderInfo?.canReorder}
                        className="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:bg-gray-400 text-white px-2.5 sm:px-3 py-2 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium flex items-center touch-manipulation"
                        title="הזמן מחדש"
                      >
                        {reorderLoading.has(order.id) ? (
                          <ArrowPathIcon className="h-4 w-4 sm:ml-1 animate-spin" />
                        ) : (
                          <ShoppingCartIcon className="h-4 w-4 sm:ml-1" />
                        )}
                        <span className="hidden sm:inline">הזמן מחדש</span>
                      </button>

                      {/* Expand Button */}
                      <button
                        onClick={() => toggleOrderExpansion(order.id)}
                        className="text-gray-400 hover:text-gray-600 p-2 touch-manipulation flex-shrink-0"
                        aria-label={expandedOrders.has(order.id) ? 'סגור' : 'פתח'}
                      >
                        {expandedOrders.has(order.id) ? (
                          <ChevronUpIcon className="h-5 w-5" />
                        ) : (
                          <ChevronDownIcon className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expanded Order Details - Mobile First */}
                {expandedOrders.has(order.id) && (
                  <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4">
                    <div className="space-y-4 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-6">
                      {/* Order Items - Mobile Optimized */}
                      <div>
                        <h4 className="text-sm sm:text-base font-medium text-gray-900 mb-2 sm:mb-3">פריטים בהזמנה</h4>
                        <div className="space-y-2 sm:space-y-3">
                          {order.items.map((item) => (
                            <div key={item.id} className="flex items-center gap-2 sm:gap-3">
                              {/* Product Image - Smaller on mobile */}
                              <div className="flex-shrink-0">
                                {item.product?.images && item.product.images.length > 0 ? (
                                  <img
                                    src={item.product.images[0]}
                                    alt={item.product.name}
                                    className="h-10 w-10 sm:h-12 sm:w-12 rounded-md object-cover"
                                  />
                                ) : (
                                  <div className="h-10 w-10 sm:h-12 sm:w-12 bg-gray-200 rounded-md flex items-center justify-center">
                                    <TagIcon className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400" />
                                  </div>
                                )}
                              </div>
                              
                              {/* Product Info */}
                              <div className="flex-1 min-w-0">
                                <p className="text-xs sm:text-sm font-medium text-gray-900 line-clamp-1">
                                  {item.product?.name || 'מוצר לא זמין'}
                                </p>
                                <p className="text-xs text-gray-500 hidden sm:block">
                                  SKU: {item.product?.sku || 'N/A'}
                                </p>
                              </div>
                              
                              {/* Quantity & Price */}
                              <div className="text-left flex-shrink-0">
                                <p className="text-xs sm:text-sm font-medium text-gray-900">
                                  {item.quantity} יח'
                                </p>
                                <p className="text-xs sm:text-sm text-gray-500">
                                  {formatPrice(item.unit_price)}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Order Summary - Mobile Optimized */}
                      <div>
                        <h4 className="text-sm sm:text-base font-medium text-gray-900 mb-2 sm:mb-3">פרטי ההזמנה</h4>
                        <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">סכום ביניים:</span>
                            <span>{formatPrice(order.subtotal)}</span>
                          </div>
                          {order.tax_amount > 0 && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">מע"מ:</span>
                              <span>{formatPrice(order.tax_amount)}</span>
                            </div>
                          )}
                          {order.shipping_amount > 0 && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">משלוח:</span>
                              <span>{formatPrice(order.shipping_amount)}</span>
                            </div>
                          )}
                          <div className="flex justify-between font-semibold text-base pt-2 border-t">
                            <span>סה"כ:</span>
                            <span>{formatPrice(order.total_amount)}</span>
                          </div>
                        </div>

                        {/* Payment & Shipping Info - Mobile Optimized */}
                        <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">אמצעי תשלום:</span>
                            <span>{order.payment_method}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">סטטוס תשלום:</span>
                            <span className={`px-2 py-0.5 sm:py-1 rounded text-xs ${
                              order.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {order.payment_status === 'paid' ? 'שולם' : 'ממתין לתשלום'}
                            </span>
                          </div>
                          {order.tracking_number && (
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">מספר מעקב:</span>
                              <span className="font-mono text-xs">{order.tracking_number}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination - Mobile First */}
        {pagination.totalPages > 1 && (
          <div className="mt-6 sm:mt-8">
            {/* Mobile: Simple prev/next */}
            <div className="flex sm:hidden justify-between gap-2">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
              >
                ← הקודם
              </button>
              <div className="flex items-center justify-center px-4 text-sm text-gray-700 bg-gray-50 border border-gray-300 rounded-lg">
                {pagination.currentPage} / {pagination.totalPages}
              </div>
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
              >
                הבא →
              </button>
            </div>

            {/* Desktop: Full pagination */}
            <div className="hidden sm:flex items-center justify-between">
              <div className="text-xs sm:text-sm text-gray-700">
                מציג {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} - {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} מתוך {pagination.totalItems} הזמנות
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  הקודם
                </button>
                
                {[...Array(pagination.totalPages)].map((_, index) => {
                  const page = index + 1;
                  const isCurrentPage = page === pagination.currentPage;
                  
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-2 text-sm font-medium rounded-lg ${
                        isCurrentPage
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  הבא
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderHistoryPage;
